import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import DynamicSectionRenderer from '../components/DynamicSectionRenderer';
import axios from 'axios';
import { getCachedSettings, cacheSettings } from '../utils/settingsCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const cachedSettings = useMemo(() => getCachedSettings(), []);
  const [settings, setSettings] = useState(cachedSettings);
  const [dynamicSections, setDynamicSections] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, testimonialsRes, settingsRes, sectionsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/sections?page=home`),
      ]);
      setServices(servicesRes.data.slice(0, 6));
      setTestimonials(testimonialsRes.data.slice(0, 3));
      const newSettings = { ...cachedSettings, ...settingsRes.data };
      setSettings(newSettings);
      cacheSettings(newSettings);
      setDynamicSections(sectionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setDataLoaded(true);
    }
  }, [cachedSettings]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = [
    { value: settings?.stat_patients || '10,000+', label_en: 'Patients Treated', label_hi: 'मरीजों का इलाज' },
    { value: settings?.stat_doctors || '25+', label_en: 'Expert Doctors', label_hi: 'विशेषज्ञ डॉक्टर' },
    { value: settings?.stat_departments || '15+', label_en: 'Departments', label_hi: 'विभाग' },
    { value: settings?.stat_availability || '24/7', label_en: 'Emergency Care', label_hi: 'आपातकालीन सेवा' },
  ];

  return (
    <div className="min-h-screen bg-white font-body" data-testid="home-page">
      <Navbar />

      {/* Hero — Auto-sized to media's natural aspect (image OR video) */}
      <div className="pt-[72px] sm:pt-[100px] lg:pt-[160px]" data-testid="hero-section">
        <div className="relative w-full max-h-[88vh] overflow-hidden">
          {settings?.homepage_media_type === 'video' && settings?.homepage_video_url ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={settings?.homepage_image_url || undefined}
              className="block w-full h-auto max-h-[88vh] object-contain bg-black"
              data-testid="hero-video"
            >
              {settings.homepage_video_url_480 && (
                <source src={settings.homepage_video_url_480} type="video/mp4" media="(max-width: 640px)" />
              )}
              <source src={settings.homepage_video_url} type="video/mp4" />
            </video>
          ) : settings?.homepage_image_url ? (
            <img
              src={settings.homepage_image_url}
              alt="Hospital"
              width={1600}
              height={900}
              className="block w-full h-auto max-h-[88vh] object-cover"
              data-testid="hero-image"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            // Neutral placeholder while real hero image loads — avoids the "old image flash" issue
            <div
              aria-hidden="true"
              className="block w-full bg-gradient-to-br from-brand-teal-tint via-white to-brand-teal-tint"
              style={{ aspectRatio: '16 / 9', maxHeight: '88vh' }}
              data-testid="hero-image-placeholder"
            />
          )}
          {/* Subtle bottom fade for elegant blend with mission section below */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 sm:h-24 bg-gradient-to-t from-white/95 via-white/40 to-transparent"></div>
        </div>
      </div>

      {/* Mission Text — Centered below hero */}
      <div className="py-20 md:py-28 bg-white" data-testid="mission-section">
        <Reveal className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl leading-relaxed text-brand-navy mb-8">
            {language === 'en'
              ? settings?.tagline_en || 'Providing quality healthcare services with compassion, expertise, and unwavering dedication to every patient.'
              : settings?.tagline_hi || 'करुणा, विशेषज्ञता और हर मरीज के प्रति अटूट समर्पण के साथ गुणवत्तापूर्ण स्वास्थ्य सेवाएँ प्रदान करना।'}
          </h1>
          <a
            href={settings?.appointment_whatsapp
              ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(language === 'en' ? 'I would like to book an appointment' : 'मैं अपॉइंटमेंट बुक करना चाहूँगा')}`
              : `/contact`}
            target={settings?.appointment_whatsapp ? "_blank" : "_self"}
            rel={settings?.appointment_whatsapp ? "noopener noreferrer" : undefined}
            className="inline-block px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            data-testid="enquire-appointment-btn"
          >
            {language === 'en' ? 'Book an Appointment' : 'अपॉइंटमेंट बुक करें'}
          </a>
        </Reveal>
      </div>

      {/* Dark Banner — Stats */}
      <div className="bg-brand-navy py-16 md:py-20" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-center text-white uppercase tracking-ultra-wide text-xs font-body font-light mb-14">
            {language === 'en' ? 'Our Numbers Speak' : 'हमारे आंकड़े बोलते हैं'}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <Reveal
                key={`${stat.label_en}-${index}`}
                delay={index * 120}
                className="text-center"
                data-testid={`stat-card-${index}`}
              >
                <div className="font-heading text-4xl sm:text-5xl font-light text-white mb-3">
                  {stat.value}
                </div>
                <div className="text-neutral-400 text-xs uppercase tracking-ultra-wide font-body">
                  {language === 'en' ? stat.label_en : stat.label_hi}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Services — Alternating image/text rows */}
      <div data-testid="services-section">
        {/* Section title banner */}
        <div className="bg-white py-20 md:py-24">
          <Reveal className="max-w-2xl mx-auto px-6 text-center">
            <p className="text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
              {language === 'en' ? 'What we offer' : 'हम क्या देते हैं'}
            </p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-brand-navy uppercase tracking-ultra-wide mb-4">
              {language === 'en' ? 'Our Services' : 'हमारी सेवाएं'}
            </h2>
            <div className="w-12 h-px bg-brand-teal mx-auto mb-5"></div>
            <p className="text-neutral-500 text-sm font-body font-light leading-relaxed">
              {language === 'en'
                ? 'Comprehensive healthcare services designed to meet all your medical needs.'
                : 'आपकी सभी चिकित्सा आवश्यकताओं को पूरा करने के लिए व्यापक स्वास्थ्य सेवाएं।'}
            </p>
          </Reveal>
        </div>

        {services.length === 0 && !dataLoaded ? (
          <div className="py-20 text-center text-neutral-400">Loading...</div>
        ) : (
          services.slice(0, 4).map((service, index) => {
            const isEven = index % 2 === 0;
            const isDark = !isEven;
            return (
              <Reveal
                key={service.id}
                threshold={0.1}
                className={`${isDark ? 'bg-brand-navy text-white' : 'bg-white text-brand-navy'}`}
                data-testid={`service-card-${index}`}
              >
                <div className={`grid grid-cols-1 md:grid-cols-2 min-h-[400px] lg:min-h-[500px]`}>
                  {/* Image — left for even, right for odd */}
                  <div className={`overflow-hidden ${!isEven ? 'md:order-2' : ''}`}>
                    {service.image_url ? (
                      <img
                        src={service.image_url}
                        alt={language === 'en' ? service.name_en : service.name_hi}
                        className="w-full h-full object-cover min-h-[300px] md:min-h-full grayscale-[0.3] contrast-110 hover:grayscale-0 transition-all duration-700"
                      />
                    ) : (
                      <div className={`w-full h-full min-h-[300px] ${isDark ? 'bg-neutral-900' : 'bg-neutral-100'}`}></div>
                    )}
                  </div>
                  {/* Text */}
                  <div className={`flex items-center ${!isEven ? 'md:order-1' : ''}`}>
                    <div className="px-10 md:px-16 lg:px-20 py-16 md:py-0">
                      <h3 className="font-heading font-light text-2xl lg:text-3xl uppercase tracking-wide mb-6">
                        {language === 'en' ? service.name_en : service.name_hi}
                      </h3>
                      <p className={`font-body font-light leading-relaxed text-sm mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                        {language === 'en' ? service.description_en : service.description_hi}
                      </p>
                      <Link
                        to="/services"
                        className={`inline-block text-xs uppercase tracking-ultra-wide font-body border-b pb-1 transition-colors ${
                          isDark ? 'text-white border-white hover:text-brand-teal-light' : 'text-brand-teal border-brand-teal hover:text-brand-teal-dark'
                        }`}
                      >
                        {language === 'en' ? 'Learn More' : 'और जानें'}
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })
        )}

        {services.length > 4 && (
          <div className="bg-white py-16 text-center">
            <Link
              to="/services"
              className="inline-block px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark transition-colors"
              data-testid="view-all-services-link"
            >
              {language === 'en' ? 'View All Services' : 'सभी सेवाएं देखें'}
            </Link>
          </div>
        )}
      </div>

      {/* Testimonials */}
      {testimonials && testimonials.length > 0 && (
        <div className="bg-brand-teal-tint py-20 md:py-28" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-6">
            <Reveal className="text-center mb-14">
              <p className="text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
                {language === 'en' ? 'In their words' : 'उनकी जुबानी'}
              </p>
              <h2 className="font-heading font-light text-3xl sm:text-4xl text-brand-navy uppercase tracking-ultra-wide mb-4">
                {language === 'en' ? 'Patient Stories' : 'मरीजों की कहानियां'}
              </h2>
              <div className="w-12 h-px bg-brand-teal mx-auto"></div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {testimonials.map((testimonial, index) => (
                <Reveal
                  key={testimonial.id}
                  delay={index * 140}
                  className="bg-white p-8 text-center hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-500"
                  data-testid={`testimonial-card-${index}`}
                >
                  <div className="flex items-center justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-brand-teal text-sm mx-0.5">&#9733;</span>
                    ))}
                  </div>
                  <p className="font-heading font-light text-base sm:text-lg leading-relaxed text-brand-navy mb-6 italic">
                    &ldquo;{language === 'en' ? testimonial.content_en : testimonial.content_hi}&rdquo;
                  </p>
                  <div className="w-8 h-px bg-brand-teal mx-auto mb-4"></div>
                  <p className="text-xs uppercase tracking-ultra-wide text-neutral-500 font-body">
                    {language === 'en' ? testimonial.patient_name_en : testimonial.patient_name_hi}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section — Dark */}
      <div className="bg-brand-navy py-24 md:py-32" data-testid="cta-section">
        <Reveal className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-ultra-wide text-brand-teal-light font-body mb-3">
            {language === 'en' ? 'Get in touch' : 'संपर्क में रहें'}
          </p>
          <h2 className="font-heading font-light text-3xl sm:text-4xl text-white uppercase tracking-ultra-wide mb-4">
            {language === 'en' ? 'Your Health, Our Priority' : 'आपका स्वास्थ्य, हमारी प्राथमिकता'}
          </h2>
          <div className="w-12 h-px bg-brand-teal-light mx-auto mb-6"></div>
          <p className="text-white/70 font-body font-light text-sm leading-relaxed mb-10 max-w-lg mx-auto">
            {language === 'en'
              ? 'Book your appointment today and experience quality healthcare with compassion and expertise.'
              : 'आज ही अपनी अपॉइंटमेंट बुक करें और करुणा और विशेषज्ञता के साथ गुणवत्तापूर्ण स्वास्थ्य सेवा का अनुभव करें।'}
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            data-testid="cta-contact-btn"
          >
            {language === 'en' ? 'Contact Us' : 'संपर्क करें'}
          </Link>
        </Reveal>
      </div>

      {/* Map Section */}
      <div className="py-24 md:py-32 bg-white" data-testid="map-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <p className="text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
              {language === 'en' ? 'Find us' : 'हमें खोजें'}
            </p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-brand-navy uppercase tracking-ultra-wide mb-4">
              {language === 'en' ? 'Visit Us' : 'हमसे मिलें'}
            </h2>
            <div className="w-12 h-px bg-brand-teal mx-auto mb-5"></div>
            <p className="text-neutral-500 text-sm font-body font-light">
              {language === 'en' ? 'Find us easily with our location map' : 'हमारे स्थान के नक्शे के साथ हमें आसानी से खोजें'}
            </p>
          </Reveal>
          {dataLoaded && settings && (
            <div>
              <div
                className="overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=SKB%20Aarogyam%20Multi-Speciality%20Healthcare', '_blank')}
              >
                <div className="relative w-full" style={{ paddingBottom: '66.6666%' }}>
                  <iframe
                    src="https://maps.google.com/maps?width=600&height=400&hl=en&q=SKB%20Aarogyam%20Multi-Speciality%20Healthcare&t=&z=14&ie=UTF8&iwloc=B&output=embed"
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                    frameBorder="0"
                    scrolling="no"
                    marginHeight="0"
                    marginWidth="0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Hospital Location"
                  ></iframe>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-xs text-neutral-400 uppercase tracking-ultra-wide font-body">
                  {language === 'en' ? 'Click on the map to get directions' : 'दिशा-निर्देश प्राप्त करने के लिए मानचित्र पर क्लिक करें'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Sections */}
      {dynamicSections.length > 0 && <DynamicSectionRenderer sections={dynamicSections} />}

      <Footer />
    </div>
  );
};

export default Home;
